import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import {
  ExclamationTriangleIcon,
  BellAlertIcon,
  SignalIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { useRealtimeStore } from '../store/realtimeStore';
import { useUnitStore } from '../store/unitStore';
import { useAuthStore } from '../store/authStore';
import UnitFilter from '../components/UnitFilter';
import RotationBoard from '../components/RotationBoard';
import clsx from 'clsx';
import { format } from 'date-fns';

const ACTIVE_SOS = ['TRIGGERED', 'ACKNOWLEDGED', 'RESPONDING'];
const ONLINE_WINDOW_MS = 15 * 60 * 1000;

function KpiTile({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  tone: 'accent' | 'danger' | 'warning' | 'neutral';
}) {
  const toneClass = {
    accent: 'text-command-accent border-command-accent/30',
    danger: 'text-red-400 border-red-500/30',
    warning: 'text-amber-400 border-amber-500/30',
    neutral: 'text-gray-200 border-command-border',
  }[tone];
  return (
    <div className={clsx('p-4 rounded-xl border bg-command-panel flex items-center gap-3', toneClass)}>
      <Icon className="w-8 h-8 opacity-80" />
      <div>
        <div className="text-2xl font-display font-bold leading-none">{value}</div>
        <div className="text-[11px] text-gray-400 font-tactical mt-1 uppercase tracking-wide">{label}</div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { sosEvents, locations, alerts, isConnected } = useRealtimeStore();
  const { getEffectiveUnitIds, isHQ, units } = useUnitStore();
  const { user } = useAuthStore();

  const effectiveUnitIds = getEffectiveUnitIds(user?.unitId || null);
  const userIsHQ = isHQ(user?.unitId || null);

  const unitName = (unitId?: string | null) =>
    (unitId && units.find(u => u.id === unitId)?.name) || 'Unit';

  const activeSOS = useMemo(() => {
    const filtered = sosEvents.filter(e => ACTIVE_SOS.includes(e.status));
    if (!effectiveUnitIds) return filtered;
    return filtered.filter(e => effectiveUnitIds.includes(e.unitId || ''));
  }, [sosEvents, effectiveUnitIds]);

  const activeAlerts = useMemo(() => {
    const filtered = alerts.filter(a => a.status === 'ACTIVE');
    if (!effectiveUnitIds) return filtered;
    return filtered.filter(a => effectiveUnitIds.includes(a.unitId || ''));
  }, [alerts, effectiveUnitIds]);

  // Live-tracked count: distinct in-scope tracking entries seen within the online window.
  const liveTracked = useMemo(() => {
    const now = Date.now();
    let count = 0;
    for (const loc of locations.values()) {
      if (effectiveUnitIds && !effectiveUnitIds.includes(loc.unitId || '')) continue;
      if (new Date(loc.serverTime).getTime() > now - ONLINE_WINDOW_MS) count += 1;
    }
    return count;
  }, [locations, effectiveUnitIds]);

  const unitsInView = effectiveUnitIds ? effectiveUnitIds.length : units.filter(u => u.role !== 'HQ').length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg font-display font-bold text-gray-100">Command Dashboard</h1>
          <p className="text-gray-500 text-[11px]">
            {userIsHQ ? 'Brigade HQ — ST-2026 operational overview' : 'ST-2026 operational overview'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {userIsHQ && <UnitFilter />}
          <div
            className={clsx(
              'px-3 py-1.5 rounded-lg text-xs font-tactical',
              isConnected
                ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
            )}
          >
            {isConnected ? '● LIVE' : '○ OFFLINE'}
          </div>
        </div>
      </div>

      {/* Critical SOS banner */}
      {activeSOS.length > 0 && (
        <div
          onClick={() => navigate('/sos')}
          className="p-3 bg-red-500/20 border border-red-500 rounded-lg cursor-pointer hover:bg-red-500/30 transition-colors sos-blink"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
              <ExclamationTriangleIcon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-red-400">{activeSOS.length} ACTIVE SOS</h3>
              <p className="text-xs text-red-300">Click to view and respond immediately</p>
            </div>
            <button className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded hover:bg-red-600">
              RESPOND
            </button>
          </div>
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile label="Live Platoons" value={liveTracked} icon={SignalIcon} tone="accent" />
        <KpiTile label="Active SOS" value={activeSOS.length} icon={ExclamationTriangleIcon} tone="danger" />
        <KpiTile label="Active Alerts" value={activeAlerts.length} icon={BellAlertIcon} tone="warning" />
        <KpiTile label="Units In View" value={unitsInView} icon={Squares2X2Icon} tone="neutral" />
      </div>

      {/* Per-farmbase 3-lane rotation board */}
      <RotationBoard unitIds={effectiveUnitIds} />

      {/* Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent SOS (PII-safe: unit + status + time only) */}
        <div className="p-5 rounded-xl border border-command-border bg-command-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-100">Recent SOS</h3>
            <button onClick={() => navigate('/sos')} className="text-sm text-command-accent hover:underline">
              View All
            </button>
          </div>
          <div className="space-y-2">
            {sosEvents.slice(0, 5).map((sos) => (
              <div
                key={sos.id}
                className={clsx(
                  'p-3 rounded-lg border flex items-center justify-between',
                  sos.status === 'TRIGGERED' && 'bg-red-500/10 border-red-500/30',
                  sos.status === 'ACKNOWLEDGED' && 'bg-yellow-500/10 border-yellow-500/30',
                  sos.status === 'RESPONDING' && 'bg-orange-500/10 border-orange-500/30',
                  sos.status === 'RESOLVED' && 'bg-green-500/10 border-green-500/30',
                  sos.status === 'CANCELLED' && 'bg-gray-500/10 border-gray-500/30'
                )}
              >
                <div>
                  <p className="text-sm font-medium text-gray-200">{unitName(sos.unitId)}</p>
                  <p className="text-xs text-gray-500">
                    {format(new Date(sos.triggeredAt), 'HH:mm:ss • dd MMM')}
                  </p>
                </div>
                <span className="px-2 py-1 rounded text-xs font-semibold bg-command-bg text-gray-300 border border-command-border">
                  {sos.status}
                </span>
              </div>
            ))}
            {sosEvents.length === 0 && (
              <p className="text-gray-500 text-sm text-center py-4">No recent SOS events</p>
            )}
          </div>
        </div>

        {/* Active alerts */}
        <div className="p-5 rounded-xl border border-command-border bg-command-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-100">Active Alerts</h3>
            <button onClick={() => navigate('/alerts')} className="text-sm text-command-accent hover:underline">
              View All
            </button>
          </div>
          <div className="space-y-2">
            {activeAlerts.slice(0, 5).map((alert) => (
              <div
                key={alert.id}
                className={clsx(
                  'p-3 rounded-lg border flex items-center justify-between',
                  alert.severity === 'CRITICAL' && 'bg-red-500/10 border-red-500/30',
                  alert.severity === 'HIGH' && 'bg-orange-500/10 border-orange-500/30',
                  alert.severity === 'MEDIUM' && 'bg-yellow-500/10 border-yellow-500/30',
                  alert.severity === 'LOW' && 'bg-blue-500/10 border-blue-500/30'
                )}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-200 truncate">{alert.title}</p>
                  <p className="text-xs text-gray-500 truncate">{alert.message}</p>
                </div>
                <span className="px-2 py-1 rounded text-xs font-semibold bg-command-bg text-gray-300 border border-command-border flex-shrink-0 ml-2">
                  {alert.severity}
                </span>
              </div>
            ))}
            {activeAlerts.length === 0 && (
              <p className="text-gray-500 text-sm text-center py-4">No active alerts</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
